import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { ENGINE_API_BASE_URL, PM_BASE_URL } from '../../constants/sites';

const PKCE_VALUE = /^[A-Za-z0-9_-]{43,128}$/;

function safeReturnPath(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('://')) {
    return null;
  }
  return value;
}

interface HandoffEnvelope {
  data?: {
    code?: string;
  };
  error?: {
    message?: string;
    requestId?: string;
  };
}

const EngineContinuePage: React.FC = () => {
  const started = useRef(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const run = async () => {
      const query = new URLSearchParams(window.location.search);
      const state = query.get('state') || '';
      const codeChallenge = query.get('code_challenge') || '';
      const returnPath = safeReturnPath(query.get('return_path'));
      const target = query.get('target');
      const platformToken = window.localStorage.getItem('token');

      if (target !== 'pm' || !returnPath || !PKCE_VALUE.test(state) || !PKCE_VALUE.test(codeChallenge)) {
        throw new Error('连接参数无效，请从云作引擎重新发起连接。');
      }
      if (!platformToken) {
        throw new Error('平台登录已失效，请重新登录后再试。');
      }

      const response = await fetch(`${ENGINE_API_BASE_URL}/api/v1/auth/handoffs`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${platformToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target: 'pm',
          returnPath,
          codeChallenge,
        }),
      });
      const payload = await response.json().catch(() => ({})) as HandoffEnvelope;
      const code = payload.data?.code;
      if (!response.ok || !code || !PKCE_VALUE.test(code)) {
        const requestSuffix = payload.error?.requestId ? `（请求编号：${payload.error.requestId}）` : '';
        throw new Error(`${payload.error?.message || '无法创建安全连接'}${requestSuffix}`);
      }

      const callback = new URL('/auth/callback', `${PM_BASE_URL}/`);
      callback.hash = new URLSearchParams({ code, state }).toString();
      window.location.replace(callback.toString());
    };

    void run().catch((reason) => {
      setError(reason instanceof Error ? reason.message : '连接云作引擎失败，请重试。');
    });
  }, []);

  return (
    <main className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-slate-50 px-6 py-20">
      <section className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm" aria-live="polite">
        {error ? (
          <>
            <AlertCircle className="mx-auto h-8 w-8 text-rose-500" />
            <h1 className="mt-5 text-xl font-semibold text-slate-950">没有完成连接</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>
            <a
              href={`${PM_BASE_URL}/documents`}
              className="mt-6 inline-flex rounded-full bg-slate-950 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              返回云作引擎
            </a>
          </>
        ) : (
          <>
            <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <h1 className="mt-5 text-xl font-semibold text-slate-950">正在安全连接云作引擎</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">即将返回你的文档空间，请不要关闭页面。</p>
          </>
        )}
      </section>
    </main>
  );
};

export default EngineContinuePage;
